import { useCallback, useEffect, useState, memo } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, ModalLayout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { listDrives, listFoldersByTenant } from '../../services/index.js';
import ChatVaultFolderTree from './ChatVaultFolderTree.jsx';
import {
  SAVE_DRIVE_FILE_TYPES,
  buildDriveSaveFile,
  saveDriveFileToDestinations,
} from './chatDocumentSaveToDrive.js';

const DRIVES_META = {
  page: 1,
  page_size: 100,
  order_by: 'created_at',
  order_direction: 'DESC',
  filters: {},
};

function parseDrivesBody(data) {
  if (Array.isArray(data?.body)) return data.body;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.body?.data)) return data.body.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function mapVaultRows(rows) {
  return (rows || []).map((item) => ({
    id: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
    title: item.name || item.title || 'Untitled drive',
    name: item.name || item.title,
    vaultUuid: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
  }));
}

/**
 * Two-step “Save to drive” flow (parity with ann `SaveToDriveModal`): file name + type, then vault/folder targets.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.documentTitle — default file name (step 1)
 * @param {import('axios').AxiosInstance} props.apiClient
 * @param {string|null} [props.userId]
 * @param {string|null} [props.tenantId]
 * @param {() => { content: string, contentFormat: string }} props.getDocumentContent
 * @param {(result: { successCount: number, firstResult?: object }) => void} [props.onSaved]
 */
const ChatSaveToDriveModal = ({
  isOpen,
  onClose,
  documentTitle = 'Document',
  apiClient,
  userId = null,
  tenantId = null,
  getDocumentContent,
  onSaved,
}) => {
  const { t } = useTranslation('chat');
  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState(documentTitle);
  const [selectedFileType, setSelectedFileType] = useState('txt');
  const [vaults, setVaults] = useState([]);
  const [expandedVaults, setExpandedVaults] = useState(() => new Set());
  const [vaultFolders, setVaultFolders] = useState({});
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [loadingVaults, setLoadingVaults] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchVaults = useCallback(async () => {
    if (!apiClient) return;
    setLoadingVaults(true);
    try {
      const [userResp, manageResp] = await Promise.all([
        listDrives(apiClient, { scope: 'USER', meta: DRIVES_META }),
        listDrives(apiClient, { scope: 'MANAGE', meta: DRIVES_META }),
      ]);
      const userRows = parseDrivesBody(userResp?.data ?? userResp ?? {});
      const manageRows = parseDrivesBody(manageResp?.data ?? manageResp ?? {});
      const merged = [...userRows, ...manageRows];
      setVaults(mapVaultRows(merged));
    } catch {
      setVaults([]);
    } finally {
      setLoadingVaults(false);
    }
  }, [apiClient]);

  useEffect(() => {
    if (!isOpen) return;
    setFileName(documentTitle);
    setSelectedFileType('txt');
    setStep(1);
    setSelectedDestinations([]);
    setExpandedVaults(new Set());
    setVaultFolders({});
    void fetchVaults();
  }, [isOpen, documentTitle, fetchVaults]);

  // Close when the user clicks on shell chrome (topbar / sidebar) — #2
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.target.closest('[data-app-shell-chrome]')) {
        onClose?.();
      }
    };
    document.addEventListener('pointerdown', handler, { capture: true });
    return () => document.removeEventListener('pointerdown', handler, { capture: true });
  }, [isOpen, onClose]);

  const fetchFoldersForVault = useCallback(
    async (vaultUuid) => {
      if (!apiClient || !vaultUuid) return;
      setLoadingFolders((prev) => ({ ...prev, [vaultUuid]: true }));
      try {
        const res = await listFoldersByTenant(apiClient, {
          parent_folder_uuid: null,
          vault_uuid: vaultUuid,
          page: 1,
          page_size: 100,
          order_by: ['updated_at'],
          order_direction: ['DESC'],
          filters: {},
          cursor: null,
        });
        const data = res?.data ?? res ?? {};
        const folders = Array.isArray(data?.body) ? data.body : Array.isArray(data) ? data : [];
        setVaultFolders((prev) => ({ ...prev, [vaultUuid]: folders }));
      } catch {
        setVaultFolders((prev) => ({ ...prev, [vaultUuid]: [] }));
      } finally {
        setLoadingFolders((prev) => ({ ...prev, [vaultUuid]: false }));
      }
    },
    [apiClient],
  );

  const handleToggleVault = useCallback(
    (vaultUuid) => {
      setExpandedVaults((prev) => {
        const next = new Set(prev);
        if (next.has(vaultUuid)) {
          next.delete(vaultUuid);
        } else {
          next.add(vaultUuid);
          if (!vaultFolders[vaultUuid]) {
            void fetchFoldersForVault(vaultUuid);
          }
        }
        return next;
      });
    },
    [fetchFoldersForVault, vaultFolders],
  );

  const handleToggleDestination = useCallback((destination) => {
    setSelectedDestinations((prev) => {
      const exists = prev.some(
        (d) =>
          d.vaultUuid === destination.vaultUuid && d.folderUuid === destination.folderUuid,
      );
      if (exists) {
        return prev.filter(
          (d) =>
            !(d.vaultUuid === destination.vaultUuid && d.folderUuid === destination.folderUuid),
        );
      }
      return [...prev, destination];
    });
  }, []);

  const handleNext = useCallback(() => {
    if (!fileName.trim()) return;
    setStep(2);
  }, [fileName]);

  const handleBack = useCallback(() => setStep(1), []);

  const handleSubmit = useCallback(async () => {
    if (!fileName.trim() || selectedDestinations.length === 0 || !apiClient) return;
    const typeDef = SAVE_DRIVE_FILE_TYPES.find((t) => t.value === selectedFileType);
    if (!typeDef) return;

    setIsSubmitting(true);
    try {
      const { content, contentFormat } = getDocumentContent?.() ?? {
        content: '',
        contentFormat: 'text',
      };
      const file = await buildDriveSaveFile(fileName.trim(), typeDef, content, contentFormat);
      const { successCount, errors, firstResult } = await saveDriveFileToDestinations(
        apiClient,
        file,
        selectedDestinations,
        { userId, tenantId },
      );
      if (successCount > 0) {
        onSaved?.({ successCount, errors, firstResult, fileName: fileName.trim() });
        onClose?.();
      } else {
        throw new Error(errors.join('\n') || 'Failed to save to any location.');
      }
    } catch (e) {
      onSaved?.({
        successCount: 0,
        errors: [e?.message || 'Save failed'],
        firstResult: null,
        fileName: fileName.trim(),
        error: e,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    apiClient,
    fileName,
    getDocumentContent,
    onClose,
    onSaved,
    selectedDestinations,
    selectedFileType,
    tenantId,
    userId,
  ]);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title={t('chat.saveToDrive.stepXOf2', { current: step })}
      closeAriaLabel={t('chat.saveToDrive.close')}
      maxWidthClassName="max-w-lg"
      heightClassName="min-h-0 max-h-[min(90vh,40rem)]"
      panelClassName="max-h-[min(90vh,40rem)]"
      useBodyPortal
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        {isSubmitting ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-b-xl bg-surface/80 backdrop-blur-[1px]"
            aria-busy="true"
          >
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
          </div>
        ) : null}

        {step === 1 ? (
          <div className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="chat-save-drive-filename" className="text-xs font-medium text-content-secondary">
                {t('chat.saveToDrive.fileName')}
              </label>
              <input
                id="chat-save-drive-filename"
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                disabled={isSubmitting}
                className="w-full rounded-lg border border-edge bg-surface px-3 py-2 text-sm text-content shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder={t('chat.saveToDrive.fileNamePlaceholder')}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="chat-save-drive-filetype" className="text-xs font-medium text-content-secondary">
                {t('chat.saveToDrive.fileType')}
              </label>
              <select
                id="chat-save-drive-filetype"
                value={selectedFileType}
                onChange={(e) => setSelectedFileType(e.target.value)}
                disabled={isSubmitting}
                className="w-full rounded-lg border border-edge bg-surface px-3 py-2 text-sm text-content shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {SAVE_DRIVE_FILE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 bg-brand-500/10 px-4 py-2.5 text-center text-sm font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-200">
              {t('chat.saveToDrive.destinationsSelected', { count: selectedDestinations.length })}
            </div>
            <div className="min-h-[200px] flex-1 border-t border-edge">
              {loadingVaults ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-content-muted">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('chat.saveToDrive.loadingVaults')}
                </div>
              ) : (
                <ChatVaultFolderTree
                  vaults={vaults}
                  vaultFolders={vaultFolders}
                  expandedVaults={expandedVaults}
                  selectedDestinations={selectedDestinations}
                  loadingFolders={loadingFolders}
                  onToggleVault={handleToggleVault}
                  onToggleDestination={handleToggleDestination}
                />
              )}
            </div>
          </div>
        )}

        <div className="flex shrink-0 justify-end gap-3 border-t border-edge bg-surface-muted/20 px-5 py-4">
          {step === 1 ? (
            <>
              <Button type="button" variant="secondary" size="md" onClick={onClose} disabled={isSubmitting}>
                {t('chat.saveToDrive.cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={handleNext}
                disabled={!fileName.trim() || isSubmitting}
              >
                {t('chat.saveToDrive.next')}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="secondary" size="md" onClick={handleBack} disabled={isSubmitting}>
                {t('chat.saveToDrive.back')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={() => void handleSubmit()}
                disabled={isSubmitting || selectedDestinations.length === 0}
                loading={isSubmitting}
              >
                {isSubmitting
                  ? t('chat.saveToDrive.saving')
                  : t('chat.saveToDrive.saveToN', { count: selectedDestinations.length })}
              </Button>
            </>
          )}
        </div>
      </div>
    </ModalLayout>
  );
};

export default memo(ChatSaveToDriveModal);
